import { Controller } from "@hotwired/stimulus"

// Generic searchable dropdown of links (SelectComponent). Opens a panel with a filter box;
// typing hides non-matching options and highlights the top match; Up/Down move the highlight
// and Enter selects it (options are links, so selecting navigates).
export default class extends Controller {
  static targets = ["panel", "search", "option", "empty"]

  toggle(event) {
    // Keep this tap from reaching the window closeOutside handler (would otherwise
    // race-close on some browsers).
    event?.stopPropagation()
    this.panelTarget.classList.toggle("hidden")
    if (!this.panelTarget.classList.contains("hidden")) {
      this.searchTarget.value = ""
      this.filter()
      // On touch devices, auto-focusing pops the keyboard and the layout shift fires a
      // ghost outside-tap that immediately re-closes the panel (iOS). Let the user tap
      // the search box themselves there.
      if (!window.matchMedia("(pointer: coarse)").matches) this.searchTarget.focus()
    }
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  closeOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    this.optionTargets.forEach((opt) => {
      opt.classList.toggle("hidden", q !== "" && !opt.dataset.name.includes(q))
    })
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", this.visibleOptions.length > 0)
    }
    this.activeIndex = 0
    this.highlight()
  }

  next(event) {
    event.preventDefault()
    this.move(1)
  }

  previous(event) {
    event.preventDefault()
    this.move(-1)
  }

  choose(event) {
    event.preventDefault()
    this.visibleOptions[this.activeIndex]?.click()
  }

  // ── helpers ──────────────────────────────────────────────────────────
  get visibleOptions() {
    return this.optionTargets.filter((o) => !o.classList.contains("hidden"))
  }

  move(delta) {
    const count = this.visibleOptions.length
    if (count === 0) return
    this.activeIndex = (this.activeIndex + delta + count) % count
    this.highlight()
  }

  highlight() {
    const visible = this.visibleOptions
    this.optionTargets.forEach((o) => o.classList.remove("bg-surface"))
    if (visible.length === 0) return
    if (this.activeIndex == null || this.activeIndex < 0 || this.activeIndex >= visible.length) {
      this.activeIndex = 0
    }
    const el = visible[this.activeIndex]
    el.classList.add("bg-surface")
    el.scrollIntoView({ block: "nearest" })
  }
}
